import React, { useState } from "react";
import { X, ArrowRight, Check } from "lucide-react";
import api, { fmtErr } from "../api";

export default function EnterpriseModal({ onClose, lang = "en" }) {
  const [form, setForm] = useState({
    contact_name: "",
    contact_email: "",
    company_name: "",
    role: "",
    employees: "",
    phone: "",
    annual_revenue: "",
    current_us_buyers: "",
    immex_status: "",
    timeline: "",
    message: "",
  });
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  function set(k, v) { setForm((p) => ({ ...p, [k]: v })); }

  async function onSubmit(e) {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      await api.post("/enterprise-leads", form);
      setSent(true);
    } catch (e) {
      setErr(fmtErr(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }

  const t = lang === "es" ? {
    title: "IMMEX 4.0 — Programa Empresarial",
    sub: "Para fabricantes IMMEX con $50M+ en ingresos anuales o relaciones complejas con compradores en EE.UU. Construcción a medida + dashboard cruzado + integración con tu ERP. 6 semanas de la firma al lanzamiento.",
    benefits: [
      "Construcción a medida bilingüe + SEO de dominio",
      "Cross-Border Data Bridge personalizada para tu cliente principal en EE.UU.",
      "Integración con tu ERP / sistema de cumplimiento",
      "Onboarding de alto contacto, visita en planta incluida",
      "Renta inicial $85K–$250K + retainer mensual",
    ],
    contact_name: "Tu nombre", contact_email: "Correo", company_name: "Empresa",
    role: "Tu rol", employees: "Empleados", phone: "WhatsApp",
    annual_revenue: "Ingresos anuales aprox (USD)", current_us_buyers: "Compradores actuales en EE.UU.",
    immex_status: "Estado IMMEX (activo / en proceso / N/A)", timeline: "Plazo objetivo",
    message: "¿Qué te gustaría lograr?",
    submit: "Solicitar llamada estratégica",
    sentT: "Solicitud recibida.",
    sentD: "Respondemos en 24h. Si encaja, agendamos una llamada de 30 min con Derick.",
  } : {
    title: "IMMEX 4.0 — Enterprise Program",
    sub: "For IMMEX manufacturers with $50M+ annual revenue or complex US-buyer relationships. Custom build + dedicated Cross-Border Data Bridge + ERP integration. Six weeks from signed contract to launch.",
    benefits: [
      "Bilingual custom build + domain-level SEO ownership",
      "Cross-Border Data Bridge customized for your top US client",
      "ERP / compliance system integration",
      "White-glove onboarding with onsite plant visit",
      "$85K–$250K initial engagement + monthly retainer",
    ],
    contact_name: "Your name", contact_email: "Email", company_name: "Company",
    role: "Your role", employees: "Employees", phone: "WhatsApp / phone",
    annual_revenue: "Approx. annual revenue (USD)", current_us_buyers: "Current US buyers",
    immex_status: "IMMEX status (active / in progress / N/A)", timeline: "Target timeline",
    message: "What would you like to achieve?",
    submit: "Request strategy call",
    sentT: "Request received.",
    sentD: "We respond within 24h. If it's a fit, we'll schedule a 30-min call with Derick.",
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto" style={{ background: "rgba(9,9,11,0.8)" }}>
      <div className="cd-card p-8 w-full max-w-3xl my-8" data-testid="enterprise-modal" style={{ background: "var(--surface)" }}>
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="overline" style={{ color: "var(--orange)" }}>ENTERPRISE · IMMEX 4.0</div>
            <h2 className="font-display text-3xl md:text-4xl mt-3 leading-[0.95]">{t.title}</h2>
          </div>
          <button type="button" onClick={onClose} data-testid="enterprise-close"><X size={20} /></button>
        </div>

        {sent ? (
          <div className="text-center py-12">
            <div className="font-display text-6xl" style={{ color: "var(--orange)" }}>✓</div>
            <h3 className="font-display text-3xl mt-4">{t.sentT}</h3>
            <p className="mt-3" style={{ color: "var(--ink-2)" }}>{t.sentD}</p>
            <button onClick={onClose} className="btn btn-dark mt-8">Close</button>
          </div>
        ) : (
          <>
            <p className="mt-6 text-base" style={{ color: "var(--ink-2)" }}>{t.sub}</p>

            <div className="mt-6 grid md:grid-cols-2 gap-2 font-mono text-xs">
              {t.benefits.map((b, i) => (
                <div key={i} className="flex items-start gap-2">
                  <Check size={12} style={{ color: "var(--orange)", marginTop: 4, flexShrink: 0 }} />
                  <span>{b}</span>
                </div>
              ))}
            </div>

            <form onSubmit={onSubmit} className="mt-8 grid md:grid-cols-2 gap-4" data-testid="enterprise-form">
              <F label={t.contact_name} val={form.contact_name} on={(v) => set("contact_name", v)} req testid="ent-name" />
              <F label={t.contact_email} val={form.contact_email} on={(v) => set("contact_email", v)} req type="email" testid="ent-email" />
              <F label={t.company_name} val={form.company_name} on={(v) => set("company_name", v)} req testid="ent-company" />
              <F label={t.role} val={form.role} on={(v) => set("role", v)} testid="ent-role" />
              <F label={t.employees} val={form.employees} on={(v) => set("employees", v)} testid="ent-employees" />
              <F label={t.phone} val={form.phone} on={(v) => set("phone", v)} testid="ent-phone" />
              <F label={t.annual_revenue} val={form.annual_revenue} on={(v) => set("annual_revenue", v)} testid="ent-revenue" />
              <F label={t.current_us_buyers} val={form.current_us_buyers} on={(v) => set("current_us_buyers", v)} testid="ent-buyers" />
              <F label={t.immex_status} val={form.immex_status} on={(v) => set("immex_status", v)} testid="ent-immex" />
              <F label={t.timeline} val={form.timeline} on={(v) => set("timeline", v)} testid="ent-timeline" />
              <div className="md:col-span-2">
                <label className="overline">{t.message}</label>
                <textarea
                  className="cd-textarea mt-2"
                  rows={3}
                  value={form.message}
                  onChange={(e) => set("message", e.target.value)}
                  data-testid="ent-message"
                />
              </div>
              {err ? <div className="md:col-span-2 font-mono text-xs px-3 py-2" style={{ background: "var(--red)", color: "white" }}>{err}</div> : null}
              <button type="submit" disabled={loading} className="md:col-span-2 btn btn-primary btn-lg" data-testid="enterprise-submit">
                {loading ? "…" : t.submit} <ArrowRight size={16} />
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

function F({ label, val, on, req, type = "text", testid }) {
  return (
    <div>
      <label className="overline">{label}{req ? " *" : ""}</label>
      <input className="cd-input-box mt-2" value={val} onChange={(e) => on(e.target.value)} type={type} required={req} data-testid={testid} />
    </div>
  );
}
